import { LinkButton } from '@monorepo/shared-lib';
import { List, Skeleton, Space } from 'antd';
import { ReactComponent as CheckIcon } from 'assets/images/icons/checkIcon.svg';
import { ReactComponent as EnterpriseIcon } from 'assets/images/icons/iconEnterpriseColored.svg';
import { ReactComponent as LargeIcon } from 'assets/images/icons/iconLargeColored.svg';
import { ReactComponent as MediumIcon } from 'assets/images/icons/iconMediumColored.svg';
import { ReactComponent as SmallIcon } from 'assets/images/icons/iconSmallColored.svg';
import { StyledCard } from 'components/Custom/card';
import { BlackSpan, CardDescBoldSpan } from 'components/Custom/spans';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { SubscriptionPlan } from 'store/api/enums';
import { PlanPricingDetails } from 'store/billing/models/planPricingDetails';

interface PlanViewerProps {
  subscriptionPlanPricingDetails?: PlanPricingDetails;
  isLoading?: boolean;
  backToChangePlan: () => void;
}

export const PlanViewer: React.FC<PlanViewerProps> = ({
  subscriptionPlanPricingDetails,
  isLoading,
  backToChangePlan,
}) => {
  const { t } = useTranslation();

  const getPlanAdvantages = (currentPlan) => {
    switch (currentPlan) {
      case SubscriptionPlan.SMALL_PLAN:
        return [
          t('billing.plan-advantages.small.entity'),
          t('billing.plan-advantages.small.documents'),
          t('billing.plan-advantages.small.vat-returns'),
          t('billing.plan-advantages.small.users'),
          t('billing.plan-advantages.small.multi-currency'),
          t('billing.plan-advantages.small.countries'),
        ];
      case SubscriptionPlan.MEDIUM_PLAN:
        return [
          t('billing.plan-advantages.medium.entities'),
          t('billing.plan-advantages.medium.documents'),
          t('billing.plan-advantages.medium.vat-returns'),
          t('billing.plan-advantages.medium.users'),
          t('billing.plan-advantages.medium.multi-currency-countries'),
          t('billing.plan-advantages.medium.view-attachments'),
        ];
      case SubscriptionPlan.LARGE_PLAN:
        return [
          t('billing.plan-advantages.large.entities'),
          t('billing.plan-advantages.large.documents'),
          t('billing.plan-advantages.large.vat-returns'),
          t('billing.plan-advantages.large.exceptions'),
          t('billing.plan-advantages.large.workflow'),
          t('billing.plan-advantages.large.attachments'),
        ];
      case SubscriptionPlan.ENTERPRISE_PLAN:
        return [
          t('billing.plan-advantages.enterprise.entities'),
          t('billing.plan-advantages.enterprise.documents'),
          t('billing.plan-advantages.enterprise.vat-returns'),
          t('billing.plan-advantages.enterprise.exceptions'),
          t('billing.plan-advantages.enterprise.workflow'),
          t('billing.plan-advantages.enterprise.attachments'),
        ];
      default:
        return [
          t('billing.plan-advantages.default.1'),
          t('billing.plan-advantages.default.2'),
          t('billing.plan-advantages.default.3'),
          t('billing.plan-advantages.default.4'),
        ];
    }
  };

  const getPlanIcon = (currentPlan) => {
    switch (currentPlan) {
      case SubscriptionPlan.SMALL_PLAN:
        return (
          <SmallIcon
            style={{
              boxShadow: '0px 4px 10px 0px #0000000D',
              borderRadius: '50px',
              width: '100px',
              height: '100px',
            }}
          />
        );
      case SubscriptionPlan.MEDIUM_PLAN:
        return (
          <MediumIcon
            style={{
              boxShadow: '0px 4px 10px 0px #0000000D',
              borderRadius: '50px',
              width: '100px',
              height: '100px',
            }}
          />
        );
      case SubscriptionPlan.LARGE_PLAN:
        return (
          <LargeIcon
            style={{
              boxShadow: '0px 4px 10px 0px #0000000D',
              borderRadius: '50px',
              width: '100px',
              height: '100px',
            }}
          />
        );
      case SubscriptionPlan.ENTERPRISE_PLAN:
        return (
          <EnterpriseIcon
            style={{
              boxShadow: '0px 4px 10px 0px #0000000D',
              borderRadius: '50px',
            }}
          />
        );
      default:
        break;
    }
  };

  return (
    <StyledCard
      width="100%"
      $borderless={false}
      $stopshadowbox
      style={{
        overflow: 'auto',
        background: 'linear-gradient(180deg, #EDFAF6 0%, #E1F4FE 100%)',
        border: 'none',
        maxHeight: '380px',
      }}
    >
      <Skeleton
        active
        loading={!subscriptionPlanPricingDetails?.name || isLoading}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            flexFlow: 'row',
            margin: '4px 7px',
            paddingTop: '2px',
          }}
        >
          <div style={{ display: 'flex', flexFlow: 'row', gap: '50px' }}>
            {getPlanIcon(subscriptionPlanPricingDetails?.name)}
            <Space
              style={{
                columnGap: 20,
                flexFlow: 'column',
                alignItems: 'flex-start',
                //marginTop: '35px',
              }}
            >
              <CardDescBoldSpan fontWeight="700" fontSize="24px">
                {t('billing.your-selected-plan')}:{' '}
                <span style={{ fontWeight: 'normal' }}>
                  {subscriptionPlanPricingDetails?.name}
                </span>
                {/* {subscriptionPlanPricingDetails?.name + ' Plan'} */}
              </CardDescBoldSpan>

              <List
                style={{ marginBottom: '15px' }}
                dataSource={getPlanAdvantages(
                  subscriptionPlanPricingDetails?.name
                )}
                split={false}
                size="small"
                renderItem={(item) => (
                  <List.Item style={{ padding: '2px 3px' }}>
                    <CheckIcon
                      style={{
                        width: '15px',
                        height: '15px',
                        marginTop: '2px',
                        flexShrink: 0,
                      }}
                    />{' '}
                    <BlackSpan>{item}</BlackSpan>
                  </List.Item>
                )}
              />
            </Space>
          </div>
          <LinkButton
            type="link"
            color='var(--color-primary, #41aaaf)'
            onClick={backToChangePlan}
            style={{ marginTop: 4 }}
          >
            {t('billing.change-plan')}
          </LinkButton>
        </div>
      </Skeleton>
    </StyledCard>
  );
};
